import React, { useState, useCallback, useRef, useEffect, memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RichUtils } from 'draft-js';
import cn from 'classnames';

import { editorStateForEditorComponent, editorStateToJsonString } from 'Shared/utils/draftJsHelpers';
import { setOptionsForInviteToWorkspaceModal } from 'Shared/actions/optionsForInviteToWorkspaceModal';
import DraftEditor from 'Shared/components/DraftEditor/DraftEditor';
import { enrichEditorStateByImageAttachment } from 'Shared/components/DraftEditor/plugins/imageAttachmentsPlugin';
import { updateEntitysOfGifBeforeSend } from 'Shared/components/DraftEditor/plugins/addGifPlugin';

import './TextEditor.scss';

type Props = {
  focused: boolean;
  body: string;
  onSave: Function;
  setFocused?: Function;
  workspaceId: string;
};

function TextEditor({ setFocused, focused, onSave, body, workspaceId }: Props) {
  const dispatch = useDispatch();
  const usersFromWorkspace = useSelector(state => state.workspaces.find(({ id }) => id === workspaceId)?.users || []);

  // Initialize editor state with a delay to ensure plugins are ready
  const [editorState, setEditorState] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Initialize editor state with a delay to ensure plugins are ready
    Promise.resolve().then(() => {
      setEditorState(editorStateForEditorComponent(body));
      setIsInitialized(true);
    });
  }, [body]);

  const editorRef = useRef(null);

  const handleSave = useCallback(() => {
    const formattedBody = editorStateToJsonString(updateEntitysOfGifBeforeSend(editorState));
    onSave(formattedBody).then(() => setFocused(false));
  }, [editorState, onSave, setFocused]);

  const handleCancelButtonClick = useCallback(() => {
    setEditorState(editorStateForEditorComponent(body));
    setFocused(false);
  }, [setFocused, body]);

  const handleKeyCommandActionsMap = useCallback(
    () => ({
      'save-block': handleSave,
      'ctrl+enter': () => setEditorState(RichUtils.insertSoftNewline(editorState)),
    }),
    [editorState, handleSave]
  );

  const handlePastedFiles = useCallback(
    async files => {
      const file = files[0];
      if (files[0]) return;
      setEditorState(await enrichEditorStateByImageAttachment(editorState, file));
    },
    [editorState]
  );

  const handleAssignedPersonAdd = useCallback(
    value => {
      if (value && !usersFromWorkspace.find(({ personId }) => personId === value.id))
        dispatch(setOptionsForInviteToWorkspaceModal({ mentionedNewEmail: value.email, workspaceId }));
    },
    [dispatch, workspaceId, usersFromWorkspace]
  );

  const focusTextEditor = useCallback(() => {
    setTimeout(editorRef.current?.focus, 0);
  }, []);

  const handleEditorChange = useCallback(value => setEditorState(value), []);

  useEffect(() => {
    if (focused) focusTextEditor();
  }, [focusTextEditor, focused, setFocused]);

  // Don't render until editor state is initialized
  if (!isInitialized || !editorState) {
    return null;
  }

  return (
    <div>
      <div className={cn('TextEditor', { 'TextEditor--focused': focused })}>
        <DraftEditor
          readOnly={!focused}
          isInlineAttachments
          imageAttachmentFullWidth
          editorState={editorState}
          onChange={handleEditorChange}
          editorRef={editorRef}
          onAssignedPersonAdd={handleAssignedPersonAdd}
          onKeyCommandActionsMap={handleKeyCommandActionsMap}
          handlePastedFiles={handlePastedFiles}
          workspaceId={workspaceId}
        />
      </div>
      {focused ? (
        <div className="d-flex justify-content-end">
          <button className="btn btn-sm text-muted mr-2" type="button" onClick={handleCancelButtonClick}>
            Cancel
          </button>
          <button className="btn btn-sm btn-primary" type="button" onClick={handleSave}>
            Save
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default memo(TextEditor);
